/* eslint-disable max-len */
/* eslint-disable array-callback-return */
import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import escape from 'lodash/escape'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import isMobileBrowser from 'is-mobile'

import 'react-tippy/dist/tippy.css'
import { Tooltip } from 'react-tippy'

import {
  Box, Button, IconWithMenu, Link, ListItem, Text,
} from '@campaignhub/suit-theme'
import { useSetState } from '@campaignhub/react-hooks'
import { useThunkDispatch } from '@campaignhub/react-hooks'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useCurrentUserV4 from '@hooks/useCurrentUserV4'
import useReduxAction from '@hooks/useReduxAction'
import useServiceJob from '@hooks/useServiceJob'
import useUploadAsset from '@hooks/useUploadAsset'

import defaultEmailRequestState from '@models/emailRequest'
import criticalPromptServices from '@enums/criticalPromptServices'
import { patchServiceJobCritical } from '@redux/modules/serviceJob'
import promptCriticalIssue from '@functions/promptCriticalIssue'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleUp, faCloudDownload, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons'

import { getEntityByName } from '@functions/getEntityByName'
import { isValidEmail } from '@functions/validations'
import { saveDocxFile } from '@functions/copyDocxFile'
import canRenderImageType from '@functions/canRenderImageType'

const loadServiceJobAssets = (serviceJobParam, loadFn) => {
  const assetIncludes = ['comments', 'file', 'histories']
  const options = {
    serviceJobs: serviceJobParam.id,
    includes: assetIncludes.join(','),
  }

  loadFn(options).then(({ errors, success }) => {
    if (!success && errors) {
      toast.warning(errors[0])
    }
  })
}

const createInitialCopyAsset = (serviceJobParam, generateFn, loadFn) => {
  generateFn(serviceJobParam).then(({ errors, success }) => {
    if (!success && errors) {
      toast.warning(errors[0])
    }

    loadServiceJobAssets(serviceJobParam, loadFn)
  })
}

const defaultState = {
  assignedToJob: false,
  enableSendToClient: false,
  pdfFlattenNotAvailable: false,
  realStudiosDetails: null,
  sendToClientTooltip: '',
  visibleList: [],
}

const Footer = (props) => {
  const {
    contents, selectedIds, showCreateAsset, showDownloadAssets, showGenerateAsset,
  } = props

  const [state, setState] = useSetState(defaultState)
  const {
    assignedToJob,
    enableSendToClient,
    pdfFlattenNotAvailable,
    realStudiosDetails,
    sendToClientTooltip,
    visibleList,
  } = state

  const {
    callbacks: { assignServiceJobStatus, refreshServiceJobClientDetails, toggleSetState },
    serviceJob,
    workflowUpdated,
  } = useContext(PageContext)
  const dispatch = useThunkDispatch()

  const useUploadAssetPayload = useUploadAsset()
  const {
    callbacks: {
      deleteAsset: deleteFnAsset,
      launchAssetUploadModal,
      updateAsset: updateFnAsset,
      createAsset: createFnAsset,
    },
    deleting: assetDeleting,
    loading: loadingAssets,
  } = useUploadAssetPayload

  const { currentUser } = useCurrentUser()
  const { currentUserV4 } = useCurrentUserV4()
  const {
    callbacks: {
      assignServiceJobStatus: createFn,
      createInitialCopyAsset: generateFn,
      loadServiceJobAssets: loadFn,
      sendToClientServiceJob: sendToClientFn,
    },
    creating: creatingJob,
    filteredServiceJobUsers,
    updating,
  } = useServiceJob({ id: serviceJob.id })

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    assetComments, assetCommentTypes, serviceJobChecklistItems, serviceJobClientDetails, serviceJobDetails, serviceJobStatuses, serviceGroups,
  } = entities

  const isCopywritingJob = serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting'

  // SAS token is required to view/link the file attachments stored against client comments.
  // Only fetched when the user can actually send to client (the only path that opens the email modal).
  const canSendToClient = visibleList.includes('client')
  useReduxAction('containerSas', 'loadContainerSas', serviceJob.campaignId, [canSendToClient], {
    shouldPerformFn: (entityReducer) => {
      const { errors, loading, result } = entityReducer
      return canSendToClient && !!serviceJob.campaignId && !loading && !errors.length && !result
    },
  })
  const { result: containerSas } = useSelector(reduxState => reduxState.containerSas)

  // Fall back to the cached token only if it hasn't expired (mirrors FullAssetScreen).
  const getValidStoredSas = () => {
    if (!serviceJob.campaignId) return ''
    const stored = sessionStorage.getItem(serviceJob.campaignId)
    if (!stored) return ''
    const expiry = decodeURIComponent(new URLSearchParams(stored).get('se') || '')
    if (expiry && DateTime.fromISO(expiry, { zone: 'utc' }) < DateTime.now().toUTC()) return ''
    return stored
  }
  const sasToken = containerSas || getValidStoredSas() || ''

  // Enforcement is triggered only by `enforce` on a Post-Production Checklist item (type 7),
  // but when triggered it requires every item across all 5 sections (types 4-8) to be checked.
  // Provider Checklist enforcement is handled separately.
  const postProdChecklistIncomplete = (() => {
    const items = Object.values(serviceJobChecklistItems || {}).filter(x => x.serviceJobId === Number(serviceJob.id))
    const enforced = items.some(x => x.serviceJobCheckListItemTypeId === 7 && x.enforce)
    if (!enforced) return false
    return items.some(x => !x.checked)
  })()

  const changeRequestCount = Number(serviceJob?.changeRequestCount || 0)
  const hasProofingOverride = currentUserV4?.flags?.includes('Proofing_Override')
  const hasProofingRestriction = changeRequestCount >= 3 && !hasProofingOverride

  useEffect(() => {
    if (serviceJobDetails) {
      const rsDetailsId = serviceJob.details?.find(x => Object.values(serviceJobDetails).find(y => y.id === x && y.entityFieldType.name === 'RealStudiosDetails'))

      setState({
        realStudiosDetails: serviceJobDetails[rsDetailsId]?.value || null,
      })
    }
  }, [serviceJobDetails])

  function escapeHtml(text) {
    text = text.replace(/&([a-zA-Z0-9#]{2,31});/g, (match) => {
      return '&amp;' + match.slice(1)
    })

    text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;')

    return text
  }

  const showEmailModal = (serviceJobParam) => {
    const copyProofFile = !showCreateAsset ? contents.sort((a, b) => b.file?.id - a.file?.id).find(x => x.file) : ''

    const clientCommentTypeId = getEntityByName(assetCommentTypes, 'ClientComment')?.id
    const changeRequestStatusId = getEntityByName(serviceJobStatuses, 'ChangeRequest')?.id
    const selectedAssetIds = serviceJobParam?.assets || []
    const rejectionNotesByAsset = contents
      .filter(asset => selectedAssetIds.includes(asset.id) && asset.serviceJobStatusId === changeRequestStatusId)
      .map((asset) => {
        const versionCount = [...new Set((asset.histories || []).map(h => h.version))].length

        const notes = Object.values(assetComments || {})
          .filter(comment => comment.assetId === asset.id && comment.assetCommentTypeId === clientCommentTypeId)
          .sort((a, b) => (a.id > b.id ? -1 : 1))
          .map((comment) => {
            const commenter = comment.details?.find(x => x.entityFieldType?.name === 'Commenter')?.value

            // Mirror the client tab: prefer the attached file (needs a SAS token) and fall
            // back to the EditOverlay (images) / ClientUpload (links) details on the comment.
            const parseDetailValues = name => (comment.details || [])
              .filter(x => x.entityFieldType?.name === name)
              .map((x) => { try { return JSON.parse(x.value)?.value } catch (e) { return null } })
              .filter(Boolean)

            const editOverlays = parseDetailValues('EditOverlay')
            const clientUploads = parseDetailValues('ClientUpload')

            let fileUri = null
            let fileType = null
            try {
              const fileDetail = (comment.details || []).find(x => x.entityFieldType?.name === 'FileDetails')
              if (fileDetail) {
                const fileDetails = JSON.parse(JSON.parse(fileDetail.value).value)
                fileUri = decodeURIComponent(fileDetails.Uri)
                fileType = fileDetails.FileType
              }
            } catch (e) { /* malformed file details */ }

            const fileIsImage = fileUri && sasToken && fileType && canRenderImageType(fileType)
            const fileIsLink = fileUri && sasToken && fileType && !canRenderImageType(fileType)
            const images = fileIsImage ? [`${fileUri}?${sasToken}`] : editOverlays
            const links = fileIsLink ? [`${fileUri}?${sasToken}`] : clientUploads

            return {
              body: escapeHtml(comment.comment || ''),
              commenter: escapeHtml(commenter ? JSON.parse(commenter).value : ''),
              version: versionCount > 1 ? comment.version : null,
              time: DateTime.fromISO(comment.createdAt, { zone: currentUser.timeZone }).toFormat('dd LLLL y, hh:mm a'),
              images,
              links,
            }
          })
        return { fileName: escapeHtml(asset.fileName || asset.file?.originalName || ''), notes }
      })
      .filter(x => x.notes.length)

    const hasRejectionNotes = rejectionNotesByAsset.length > 0

    const rejectionNotesHtml = hasRejectionNotes ? `
        <div style="flex-direction:row;margin-top:10px;">
          <span>Client Rejection Notes:</span>
          <div style="border:1px solid #dee2e6;border-radius:4px;padding:10px;margin-top:10px;max-height:30vh;overflow:auto;">
            ${rejectionNotesByAsset.map(group => `
            <div style="margin-bottom:12px;">
              <div style="font-weight:600;font-size:14px;">${group.fileName}</div>
              <ul style="margin:4px 0 0 0;padding-left:18px;">
                ${group.notes.map((note, index) => `
                <li style="font-size:13px;padding:8px 0;${index < group.notes.length - 1 ? 'border-bottom:1px dashed #dee2e6;' : ''}">
                  <div style="font-size:13px;font-weight:500;">${note.commenter || '-'}${note.version ? ` (Version ${note.version})` : ''}</div>
                  <div style="font-size:13px;margin-top:4px;">${note.body}</div>
                  ${note.images.map((image) => { const src = escape(image); return `
                  <a href="${src}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:6px;margin-right:6px;">
                    <img src="${src}" style="max-width:120px;max-height:90px;border-radius:4px;border:1px solid #dee2e6;object-fit:cover;" />
                  </a>` }).join('')}
                  ${note.links.map(link => `
                  <a href="${escape(link)}" target="_blank" rel="noopener noreferrer" style="display:block;font-size:12px;margin-top:4px;color:#02b7e2;">› View attachment</a>`).join('')}
                  <div style="font-size:12px;color:#8c8c8e;margin-top:4px;">${note.time}</div>
                </li>`).join('')}
              </ul>
            </div>`).join('')}
          </div>
          <div style="flex-direction:row;margin-top:10px; align-items: center;">
            <input type="checkbox" id="rejectionCheckbox" style="margin: 0;" />
            <label for="rejectionCheckbox" style="margin-left: 5px; font-size: 12px;">I confirm that I've reviewed the rejection notes and everything has been addressed in this proof</label>
          </div>
        </div>` : ''

    swal.fire({
      title: 'Send Email',
      html: `
        <style>
        .swal-link-button {
          text-decoration: none;
          background-color: #f8f9fa;
          color: #212529;
          padding: 8px 12px;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 14px;
          transition: background-color 0.15s ease-in-out;
          display: inline-block;
        }
        .swal-link-button:hover {
          background-color: #e9ecef;
          color: #212529;
        }
        </style>
        <div style="flex-direction:column; text-align: left;">
        <div style="flex-direction:row;">
          <span>To:</span>
          <input id="emailTo" class="swal2-input"
            style="margin: 10px auto 0 auto; font-size: 16px; font-family: inherit;"
          />
          <span style="font-size: 12px; color: #8c8c8e">Separate with ; for multiple emails</span>
        </div>
        <div style="flex-direction:row;margin-top:10px;">
          <span>Proofing Notes:</span>
          <input id="emailProofingNotes" class="swal2-input"
            style="margin: 10px auto 0 auto; font-size: 16px; font-family: inherit;"
          />
          <span style="font-size: 12px; color: #8c8c8e">Note: This will be added to the email to be sent.</span>
        </div>
        ${rejectionNotesHtml}
        ${copyProofFile ? `
        <div style="flex-direction:row;margin-top:10px;">
            <a href="${copyProofFile.assetUrl}" target="_blank" rel="noopener noreferrer" class="swal-link-button">› View generated file</a>
        </div>` : ''}
      </div>`,
      icon: 'info',
      showDenyButton: copyProofFile,
      showCancelButton: true,
      confirmButtonText: 'Send Now',
      denyButtonText: 'Send Manually',
      willOpen: () => {
        const emailTo = serviceJobClientDetails[serviceJob.id]?.emailList || ''
        swal.getHtmlContainer().querySelector('#emailTo').value = emailTo

        const confirmButton = swal.getConfirmButton()
        const rejectionCheckbox = swal.getHtmlContainer().querySelector('#rejectionCheckbox')

        const updateConfirmState = () => {
          confirmButton.disabled = !!rejectionCheckbox && !rejectionCheckbox.checked
        }

        if (rejectionCheckbox) rejectionCheckbox.addEventListener('change', updateConfirmState)
        updateConfirmState()
      },
      preConfirm: () => {
        const emailTo = swal.getHtmlContainer().querySelector('#emailTo').value
        const rejectionCheckbox = swal.getHtmlContainer().querySelector('#rejectionCheckbox')

        if (!copyProofFile && !showCreateAsset) swal.showValidationMessage('No generated file.')
        if (!emailTo) swal.showValidationMessage('Email is required.')
        if (emailTo && emailTo.split(';').find(x => !isValidEmail(x.trim()))) {
          swal.showValidationMessage('Found an invalid email.')
        }
        if (rejectionCheckbox && !rejectionCheckbox.checked) {
          swal.showValidationMessage('Please confirm you have reviewed the rejection notes.')
        }
      },
    })
      .then((result) => {
        let emailProofingNotes = swal.getHtmlContainer().querySelector('#emailProofingNotes').value?.trim() || ''
        if (emailProofingNotes) emailProofingNotes = escapeHtml(emailProofingNotes)

        if (result.isConfirmed) {
          const emailTo = swal.getHtmlContainer().querySelector('#emailTo').value

          const emailToArray = []
          emailTo.split(';').map((x) => {
            if (isValidEmail(x.trim())) {
              emailToArray.push({
                email: x.trim(),
                name: x.trim(),
              })
            }
          })

          const emailRequestParams = {
            ...defaultEmailRequestState,
            tos: emailToArray,
            emailProofingNotes,
          }

          sendToClientFn({
            ...serviceJobParam,
            ...emailRequestParams,
          }).then(({ success, errors }) => {
            if (!success) {
              return toast.warning(errors)
            }
            toggleSetState({ workflowUpdated: !workflowUpdated })
          })
        }

        if (result.isDenied && copyProofFile?.assetUrl) {
          const link = document.createElement('a')
          link.href = copyProofFile.assetUrl
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      })
  }

  const handleSendToClient = () => {
    if (hasProofingRestriction) {
      swal.fire({
        title: 'Proofing Restricted',
        text: 'This job has already reached the maximum number of rejections allowed. Please flag this with your manager to proceed',
        icon: 'warning',
      })
      return
    }

    sendSelected(
      getEntityByName(serviceJobStatuses, 'SentToClient'),
      !showCreateAsset,
      realStudiosDetails !== null || serviceJob?.workbaseReferenceId,
    )
  }

  const generateAsset = () => {
    if (criticalPromptServices.includes(serviceJob.service?.id) && currentUser?.userRole?.userTypeId === 4) {
      promptCriticalIssue(serviceJob?.campaign?.criticalNote || '').then(({ shouldContinue, isCritical, criticalNote }) => {
        if (!shouldContinue) return

        if (isCritical) {
          const serviceJobParam = {
            id: serviceJob.id,
            serviceJob,
            payload: {
              isCritical: true,
              criticalNote,
            }
          }

          dispatch(patchServiceJobCritical(serviceJobParam, {}))
        }

        createInitialCopyAsset(serviceJob, generateFn, loadFn)
      })
      return
    }

    createInitialCopyAsset(serviceJob, generateFn, loadFn)
  }

  const sendSelected = (status, sendAll, sendEmail) => {
    const selectedAssets = sendAll ? contents.map(x => x.id) : selectedIds
    const serviceJobParam = {
      id: serviceJob.id,
      serviceJobStatusId: status.id,
      workflowComment: 'Updated from CH Assets',
      assets: selectedAssets,
    }

    const getStatusDisplay = () => {
      switch (status.name) {
        case 'InQC1':
          return 'QC1'
        case 'InQC':
          return 'QC2'
        case 'InProduction':
          return !showCreateAsset ? 'copywriter' : 'editor'
        case 'SentToClient':
          return 'client'
        case 'SentForVerification':
          return 'verification'
        default: return 'status'
      }
    }

    const statusName = getStatusDisplay()

    if (sendEmail) {
      if (serviceJobClientDetails[serviceJob.id]) {
        showEmailModal(serviceJobParam)
      }
      else {
        toast.warning('Failed to retrieve emails. Please try again.')
        refreshServiceJobClientDetails()
      }
    } else {
      swal.fire({
        title: `Send to ${statusName[0].toUpperCase() + statusName.substring(1)}`,
        html: `This action will deliver the asset to ${statusName}.`
          + '<br/>Do you wish to proceed?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        confirmButtonColor: '#e2001a',
        showClass: 'slide-from-top',
      }).then(({ value }) => {
        if (value) {
          assignServiceJobStatus(serviceJobParam, createFn)
        }
      })
    }
  }

  const confirmDelete = (deleteAll) => {
    const selectedAssets = deleteAll ? contents : selectedIds
    swal.fire({
      title: `Delete ${deleteAll ? '' : 'Selected'} Assets?`,
      text: serviceJob.serviceGroupId === 2
        ? `Are you sure you want to delete ${deleteAll ? '' : 'selected'} assets? Assets sent for client approval / Approved will not be removed from the client's library.`
        : `Are you sure you want to delete ${deleteAll ? '' : 'selected'} assets? Approved assets will not be removed from the client's library.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
      .then(({ value }) => {
        if (value) {
          selectedAssets.forEach((entity) => {
            const obj = deleteAll ? entity : { id: entity }
            deleteFnAsset(obj).then(({ success, errors }) => {
              if (!success) {
                toast.warning(errors)
              }
            })
          })
        }
      })
  }

  useEffect(() => {
    let isAssigned = filteredServiceJobUsers?.find(x => x.user.id === currentUser.id)
    if (serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting' && !isAssigned) {
      const details = Object.values(serviceJobDetails).filter(x => serviceJob?.details?.includes(x.id))
      const serviceUser = details.find(x => x.entityFieldType.name === 'ServiceUsername')?.value || ''

      if (serviceUser !== '') {
        const ctUsername = currentUser.userIdentity?.ctUsername || ''
        const serviceUsername = JSON.parse(serviceUser).value
        isAssigned = serviceUsername.toLowerCase() === ctUsername.toLowerCase()
      }
    }
    setState({ assignedToJob: isAssigned })
  }, [serviceJob, filteredServiceJobUsers, serviceJobDetails])

  useEffect(() => {
    const list = []
    const adminRoles = ['ProductionManager'] // ToDo: add as property in userRole

    if (assignedToJob || isCopywritingJob || adminRoles.includes(currentUser.userRole.name)) {
      if (currentUser.serviceJobStatuses.find(x => x === getEntityByName(serviceJobStatuses, 'InQC1')?.id) || currentUser.serviceJobStatuses.find(x => x === getEntityByName(serviceJobStatuses, 'InQC')?.id)) {
        list.push('qc')
      }
      if (currentUser.serviceJobStatuses.find(x => x === getEntityByName(serviceJobStatuses, 'InProduction')?.id)) {
        list.push('editor')
      }
      if (currentUser.serviceJobStatuses.find(x => x === getEntityByName(serviceJobStatuses, 'SentToClient')?.id)) {
        list.push('client')
      }
      if (currentUser.serviceJobStatuses.find(x => x === getEntityByName(serviceJobStatuses, 'SentForVerification')?.id)
        && !isCopywritingJob) {
        list.push('verification')
      }
    }

    setState({ visibleList: list })
  }, [assignedToJob, isCopywritingJob, serviceJobStatuses])

  useEffect(() => {
    let enabled = (showCreateAsset ? selectedIds : contents)?.length > 0

    const someFlattenNotAvailable = contents
      ?.filter((x) => (showCreateAsset ? selectedIds.includes(x.id) : true))
      ?.filter((x) => x.file?.fileType?.name?.toLowerCase() === 'pdf' && x?.file?.pdfFlattenRequests?.length > 0)
      ?.some((x) => !x.file?.pdfFlattenRequests?.some((y) => y.generatedAt !== null))

    if (serviceJob?.workbaseReferenceId && !['Copywriting', 'Photography', 'Virtual Tour'].includes(serviceGroups[serviceJob.serviceGroupId]?.name) && !pdfFlattenNotAvailable) {
      const selectedRequests = contents.filter(x => selectedIds.includes(x.id)).map(x => ({ id: x.id, previews: x.file?.filePreviewRequests }))
      const premiumNeededServices = ['Floorplan', 'Virtual Staging'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)

      const hasMissingPreview = selectedRequests?.some(f => !f?.previews?.some(x => x.imageType === 'Preview' && x.generatedAt))
      const hasMissingPremium = selectedRequests?.some(f => !f?.previews?.some(x => x.imageType === 'Premium' && x.generatedAt))

      const validPreviews = !hasMissingPreview && (!premiumNeededServices || !hasMissingPremium)
      enabled = enabled && validPreviews
    }

    const tooltipValue = someFlattenNotAvailable ? 'Some selected files are still being flattened. Please wait for the final version and refresh your screen.'
      : enabled ? '' : selectedIds?.length > 0 ? 'Some selected files have no preview generated' : 'No files selected'

    setState({ enableSendToClient: enabled, pdfFlattenNotAvailable: someFlattenNotAvailable, sendToClientTooltip: tooltipValue })
  }, [serviceJob, selectedIds, contents])

  const isMobile = isMobileBrowser()
  const showDeleteAssets = (serviceGroups[serviceJob.serviceGroupId]?.name !== 'Copywriting'
    || (serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting' && ['ProductionManager', 'QC', 'Specialist'].includes(currentUser?.userRole.name)))

  return (
    <ListItem
      boxProps={{
        borderTop: '1px solid',
        padding: 'large',
        lineHeight: '1.3',
        alignItems: 'center',
      }}
      flexDirection="row"
      style={{ cursor: 'pointer' }}
      disableHover
    >
      <Box flexDirection="column" width="100%">
        <Box flexDirection={['column', 'row']} width="100%">
        {showCreateAsset && (
          <Box
            alignItems="center"
            marginRight={[0, 'auto']}
            marginBottom={['small', 0]}
            justifyContent="flexStart"
            width="auto"
            flexDirection="column"
          >
            <Button
              buttonStyle="secondary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={launchAssetUploadModal}
              size="medium"
            >
              Create Asset
            </Button>
          </Box>
        )}
        {showGenerateAsset && (
          <Box
            alignItems="center"
            marginRight={[0, 'auto']}
            marginBottom={['small', 0]}
            justifyContent="flexStart"
            width="auto"
            flexDirection="column"
          >
            <Button
              size="medium"
              buttonStyle="secondary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              loading={creatingJob || loadingAssets}
              onClick={generateAsset}
            >
              Generate Asset
            </Button>
          </Box>
        )}
        {showDeleteAssets && (
          <Box
            alignItems="center"
            marginLeft={[0, 'auto']}
            marginBottom={['small', 0]}
            justifyContent="flexEnd"
            width="auto"
            flexDirection="column"
          >
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={showCreateAsset ? !selectedIds.length : !contents.length}
              loading={assetDeleting}
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => confirmDelete(!showCreateAsset)}
            >
              Delete {showCreateAsset && 'Selected'} Assets
            </Button>
          </Box>
        )}
        {showDownloadAssets && (
          <Box
            alignItems="center"
            marginLeft={[0, 'medium']}
            marginBottom={['small', 0]}
            justifyContent="flexEnd"
            width="auto"
            flexDirection="column"
          >
            <Button
              size="medium"
              buttonStyle="secondary"
              icon={<FontAwesomeIcon icon={faCloudDownload} />}
            >
              Download Assets
            </Button>
          </Box>
        )}
        <Box
          alignItems="center"
          marginLeft={[0, showDeleteAssets ? 'medium' : 'auto']}
          marginBottom={['small', 0]}
          justifyContent="flexEnd"
          width="auto"
          flexDirection="column"
        >
          <IconWithMenu
            icon={(
              <Button
                buttonStyle="ghostEdit"
                disabled={(showCreateAsset ? !selectedIds.length : !contents.length) || (!visibleList.length)}
                icon={<FontAwesomeIcon icon={faAngleUp} />}
                iconPosition="right"
                loading={updating}
                size="medium"
              >
                Send
              </Button>
            )}
            boxProps={{ width: ['100%', 'auto'] }}
          >
            <IconWithMenu.Menu
              closeOnClick
              listStyle={{ right: 0, left: `${isMobile ? '-1' : '-55'}px`, top: `-${(38 - visibleList.length) * visibleList.length}px` }}
              nibStyle={{ top: `${(23.5 + visibleList.length) * visibleList.length}px`, transform: 'rotate(225deg)' }}
            >
              {visibleList.includes('qc') && (
                <Link onClick={() => {
                  sendSelected(getEntityByName(serviceJobStatuses, serviceJob.serviceJobStatusId === getEntityByName(serviceJobStatuses, 'InQC1')?.id ? 'InQC' : 'InQC1'), !showCreateAsset)
                  if (serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting') {
                    const copyProofFile = contents.find(x => x.file)
                    if (copyProofFile) {
                      saveDocxFile(serviceJob, serviceJobDetails, {}, createFnAsset, updateFnAsset, copyProofFile, () => { }, false)
                    }
                  }
                }}
                >
                  {serviceJob.serviceJobStatusId === getEntityByName(serviceJobStatuses, 'InQC1')?.id ? 'Send to QC2' : 'Send to QC1'}
                </Link>
              )}
              {visibleList.includes('editor') && (
                <Link onClick={() => sendSelected(getEntityByName(serviceJobStatuses, 'InProduction'), !showCreateAsset)}>
                  Send to {!showCreateAsset ? 'Copywriter' : 'Editor'}
                </Link>
              )}
              {visibleList.includes('verification') && (
                <Link onClick={() => sendSelected(getEntityByName(serviceJobStatuses, 'SentForVerification'), !showCreateAsset)}>
                  Send to Specialist
                </Link>
              )}
            </IconWithMenu.Menu>
          </IconWithMenu>
        </Box>
        {visibleList.includes('client') && (
          <Box
            alignItems="center"
            marginLeft={[0, 'medium']}
            marginBottom={['small', 0]}
            justifyContent="flexEnd"
            width="auto"
            flexDirection="column"
          >
            <Tooltip
              html={<Text fontSize="small">{sendToClientTooltip}</Text>}
              trigger="mouseenter"
              arrow
              arrowSize="regular"
              duration={0}
              disabled={!sendToClientTooltip}
            >
              <Button
                buttonStyle="ghostEdit"
                disabled={!enableSendToClient || pdfFlattenNotAvailable || postProdChecklistIncomplete}
                iconPosition="right"
                loading={updating}
                onClick={handleSendToClient}
                size="medium"
                title={!enableSendToClient && selectedIds.length > 0 ? 'Generating previews. Please refresh after a few seconds.' : ''}
              >
                Send to Client
              </Button>
            </Tooltip>
          </Box>
        )}
        </Box>
        {postProdChecklistIncomplete && visibleList.includes('client') && (
          <Box marginTop="small" width="100%">
            <Text color="bodyFontLightColor" fontSize="xsmall" style={{ fontStyle: 'italic', textAlign: 'right', width: '100%' }}>
              Proofing will be enabled once the post-production checklist is completed.
            </Text>
          </Box>
        )}
      </Box>
    </ListItem>
  )
}

Footer.propTypes = {
  contents: PropTypes.array,
  selectedIds: PropTypes.array,
  showCreateAsset: PropTypes.bool,
  showDownloadAssets: PropTypes.bool,
  showGenerateAsset: PropTypes.bool,
}

export default Footer
